import { useState, useRef, useCallback } from 'react';
import RecordRTC from 'recordrtc';
import { MediaRecordingStatus } from '../types/media.enums';
import { RecordingResult, RecordingOptions } from '../types/recordrtc.types';
import {
  createVideoConstraints,
  createAudioConstraints,
  cleanupMediaStream,
  requestMediaStream,
  createRecordingOptions,
  isChromeOnIOS,
  isIOSSafari,
  createLogger,
} from '../utils';

/**
 * Modern video recording hook using RecordRTC directly with iOS Chrome fixes
 */
export const useVideoRecordRtc = () => {
  const [status, setStatus] = useState<MediaRecordingStatus>(MediaRecordingStatus.IDLE);
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentStream, setCurrentStream] = useState<MediaStream | null>(null);

  // Remove device enumeration - handled at higher level

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<RecordRTC | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Create logger instance
  const logger = createLogger(true);

  // Safe cleanup of stream
  const cleanupStream = useCallback(() => {
    if (mediaStreamRef.current) {
      cleanupMediaStream(mediaStreamRef.current);
      mediaStreamRef.current = null;
    }
    setCurrentStream(null);
  }, []);

  // Remove device enumeration - handled at higher level

  // Initialize camera with proper error handling
  const initializeCamera = useCallback(
    async (options: RecordingOptions = {}) => {
      try {
        setStatus(MediaRecordingStatus.ACQUIRING_MEDIA);
        cleanupStream();

        // Use constraints-based approach (facingMode)
        logger.info('Using constraints-based stream with facingMode');
        const constraints = {
          video: createVideoConstraints(options),
          audio: createAudioConstraints(options.audio),
        };
        const stream = await requestMediaStream(constraints);

        mediaStreamRef.current = stream;
        setCurrentStream(stream);
        setStatus(MediaRecordingStatus.IDLE);

        return stream;
      } catch (error: any) {
        logger.error('Error initializing camera:', error);
        setStatus(MediaRecordingStatus.ERROR);
        throw error;
      }
    },
    [cleanupStream],
  );

  // Start recording
  const startRecording = useCallback(
    async (options: RecordingOptions = {}, existingStream?: MediaStream): Promise<void> => {
      let stream = existingStream;

      if (!stream) {
        // Only create new stream if none provided
        if (!mediaStreamRef.current) {
          await initializeCamera(options);
        }
        stream = mediaStreamRef.current || undefined;
      } else {
        // Use the provided stream
        logger.info('Using existing stream for recording');
        mediaStreamRef.current = stream;
        setCurrentStream(stream);
        setStatus(MediaRecordingStatus.IDLE);
      }

      if (!stream) {
        throw new Error('No media stream available');
      }

      try {
        const recordingOptions = createRecordingOptions(options.quality, options.audio !== false);

        const recorder = new RecordRTC(stream, recordingOptions);
        recorderRef.current = recorder;

        recorder.startRecording();
        setStatus(MediaRecordingStatus.RECORDING);
        setRecordingTime(0);

        // Start timer
        intervalRef.current = setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
      } catch (error) {
        logger.error('Error starting recording:', error);
        setStatus(MediaRecordingStatus.ERROR);
        throw error;
      }
    },
    [initializeCamera],
  );

  // Stop recording with iOS Chrome fixes
  const stopRecording = useCallback((): Promise<RecordingResult> => {
    return new Promise((resolve, reject) => {
      if (
        !recorderRef.current ||
        (status !== MediaRecordingStatus.RECORDING && status !== MediaRecordingStatus.PAUSED)
      ) {
        reject(new Error('No active recording'));
        return;
      }

      setStatus(MediaRecordingStatus.STOPPING);
      const isIOS = isIOSSafari() || isChromeOnIOS();

      // iOS Chrome needs a small delay before stopping to prevent NotAllowedError
      const stopWithDelay = async () => {
        if (isIOS) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        recorderRef.current!.stopRecording(() => {
          try {
            const blob = recorderRef.current!.getBlob();
            const url = URL.createObjectURL(blob);

            const result: RecordingResult = {
              blob,
              url,
              duration: recordingTime * 1000, // Convert to milliseconds
              mimeType: blob.type,
              size: blob.size,
            };

            // Cleanup timer
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }

            setStatus(MediaRecordingStatus.STOPPED);
            resolve(result);
          } catch (error) {
            setStatus(MediaRecordingStatus.ERROR);
            reject(error);
          }
        });
      };

      stopWithDelay().catch(reject);
    });
  }, [status, recordingTime]);

  // Pause recording
  const pauseRecording = useCallback(async (): Promise<void> => {
    if (recorderRef.current && status === MediaRecordingStatus.RECORDING) {
      try {
        recorderRef.current.pauseRecording();
        setStatus(MediaRecordingStatus.PAUSED);

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } catch (error) {
        logger.error('Error pausing recording:', error);
        setStatus(MediaRecordingStatus.ERROR);
        throw error;
      }
    }
  }, [status]);

  // Resume recording
  const resumeRecording = useCallback(async (): Promise<void> => {
    if (recorderRef.current && status === MediaRecordingStatus.PAUSED) {
      try {
        recorderRef.current.resumeRecording();
        setStatus(MediaRecordingStatus.RECORDING);

        // Restart timer
        intervalRef.current = setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
      } catch (error) {
        logger.error('Error resuming recording:', error);
        setStatus(MediaRecordingStatus.ERROR);
        throw error;
      }
    }
  }, [status]);

  // Complete cleanup
  const cleanup = useCallback(() => {
    if (recorderRef.current) {
      try {
        if (status === MediaRecordingStatus.RECORDING || status === MediaRecordingStatus.PAUSED) {
          recorderRef.current.stopRecording(() => {
            recorderRef.current?.destroy();
            recorderRef.current = null;
          });
        } else {
          recorderRef.current.destroy();
          recorderRef.current = null;
        }
      } catch (error) {
        logger.warn('Error destroying recorder:', error);
      }
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    cleanupStream();
    setStatus(MediaRecordingStatus.IDLE);
    setRecordingTime(0);
  }, [status, cleanupStream]);

  return {
    // State
    status,
    recordingTime,
    currentStream,
    isRecording: status === MediaRecordingStatus.RECORDING,
    isPaused: status === MediaRecordingStatus.PAUSED,

    // Actions
    initializeCamera,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cleanup,
  };
};
